// §4 S11 "Targets & settings" `/waste/settings` — the slice-05-A half.
//
// WHAT IS HERE: the category, cause and price-class catalog editors (with
// pack-seeded rows LOCKED by default behind an explicit "Customize" unlock) and
// the matcher/variance settings form (exact tolerance, max overage %,
// reservation TTL, wire-diameter band, tenant variance tolerance, and the two
// pack-seeded knobs).
//
// WHAT IS NOT, and is therefore not drawn as a disabled shell: the recovery
// TARGETS grid (facility × category × month). Its table does not exist yet —
// it belongs to the variance slice — and a greyed-out grid promising a feature
// that has no storage is exactly the "looks done, is not" failure the doctrine
// rules out (D-033). The screen says which half it is, in one line, in the
// user's language.
//
// THE LOCK IS NOT DECORATION. A pack- or system-owned row keeps receiving pack
// corrections at every re-install; editing it makes the row the tenant's and
// stops that forever (the server demotes `source` in the writing statement,
// D-062 guard 1). "Customize" is the moment that trade is explained and
// accepted — so it is a deliberate click, not a hover state.
//
// EVERY FIELD THE ENDPOINTS ACCEPT IS REACHABLE (completed 2026-08-03, gap 4).
// The first cut of this screen sent `name + disposition` for a category,
// `is_active` for a cause and `discount + band` for a price class, so most of
// the three update schemas could not be driven by a human at all. Three of the
// holes were load-bearing rather than cosmetic:
//   · `sort_order` — spec 05 §2.2 added it precisely because without it the
//     second tap of the floor's 3-tap flow is ordered by `code`, i.e.
//     alphabetically IN ENGLISH, on a Russian floor. A group_admin had no way
//     to fix that order.
//   · price-class `is_active` — spec 05 §2.7 "Deactivating a class frees its
//     band" is the documented escape from a band dead-end (two classes cannot
//     both claim 100 m). It was implemented and tested server-side and had no
//     control at all.
//   · cause `category_ids` — the whole "restrict a reason to certain waste
//     types" feature was unreachable.
//
// ARCHIVING IS NOT REDEFINING. `is_active` is on NEITHER pack-owned column list
// (waste-catalog.repo.ts `PACK_OWNED_*_FIELDS`; spec 05 §2.1 "archiving a
// category is a lifecycle decision, not a redefinition"), and the repo's UPDATE
// demotes `source` only when a pack-owned column genuinely changes — an
// is_active-only PUT leaves a pack row pack-owned. So the on/off control is
// offered on every row, locked or not; only EDIT sits behind "Customize".
// Putting the toggle behind the unlock would have made turning off a pack
// category cost the tenant its pack updates forever, for nothing.
//
// UX-4 — ON THE REGISTER (design-system spec §3, D-107). The three catalogs are
// §3.3 `DataTable`s, so the ≤768 px collapse contract is the primitive's rather
// than this area's forty-line copy of it; the tab strip is §3.10 `NavTabs`
// (buttons carrying `aria-current="page"` — the shape M05-S15 already drove);
// every control is `Field` / `CheckboxField` / `Button`; the owner and flag
// pills are `StatusChip`. The cells still hold their editors INLINE rather than
// in a row expansion, deliberately: four certified legs address Save and Cancel
// as children of the row they edit (`row.getByRole("button", {name: "Save"})`),
// and an expansion panel is a sibling `<tr>` — the same element, a different
// subtree, and a walkthrough rewritten for no user-visible gain.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  normalizeLocale,
  SOURCE_LOCALE,
  SUPPORTED_LOCALES,
  type Locale,
} from "@assessly/contracts/locales";
import { PageHeader } from "../../../../components/breadcrumbs";
import { RowMenu } from "../../../../components/row-menu";
import { CATALOG_PAGE_SIZE, catalogPage } from "../lib/catalog-page";
import { FilterBar } from "../../../../components/filter-bar";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { CheckboxField, Field } from "../../../../components/field";
import { Select } from "../../../../components/select";
import { NavTabs } from "../../../../components/tabs";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { useStickyOpen } from "../../../../lib/use-sticky-open";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useWastePermissions } from "../hooks/permissions";
import {
  useCreatePriceClass,
  useCreateWasteCategory,
  useCreateWasteCause,
  useRemnantPriceClasses,
  useUpdatePriceClass,
  useUpdateWasteCategory,
  useUpdateWasteCause,
  useUpdateWasteSettings,
  useWasteCatalog,
  useWasteSettings,
  type RemnantPriceClass,
  type RemnantPriceClassCreate,
  type RemnantPriceClassUpdate,
  type WasteCategory,
  type WasteCategoryCreate,
  type WasteCategoryUpdate,
  type WasteCauseCode,
  type WasteCauseCreate,
  type WasteCauseUpdate,
  type WasteSettings,
} from "../hooks/waste-queries";
import "../waste.css";

type Tab = "categories" | "causes" | "classes" | "matcher";

/** The product locales, in the registry's order, which is the order S11
 * shows them. `en` is required by the contract; the rest are optional and
 * their ABSENCE is shown as absence — copying the English string into the ru
 * box to fill the form is the fake translation this product refuses to
 * manufacture. The list is the registry's (`@assessly/contracts/locales`),
 * never spelled here. */
const LOCALES = SUPPORTED_LOCALES;

type LocaleName = { en: string } & Partial<Record<Exclude<Locale, "en">, string>>;

/** The name boxes always on the form: the required English and the launch
 * market's Russian. The rest sit behind "Other languages". */
const ALWAYS_SHOWN: ReadonlySet<Locale> = new Set<Locale>([SOURCE_LOCALE, "ru"]);

/** The closed enums, taken from the generated client so a contract change that
 * adds a disposition or a floor policy fails the build here instead of
 * silently shipping a picker that is missing an option. */
type Disposition = WasteCategory["default_disposition"];
type FloorPolicy = RemnantPriceClass["floor_price_policy"];

function localeNameOf(row: { name_i18n: LocaleName }): LocaleName {
  return row.name_i18n;
}

/** What the row shows when the viewer's locale has no translation: the English,
 * marked, never a blank cell. */
function displayName(name: LocaleName, locale: string): { text: string; fallback: boolean } {
  const key = normalizeLocale(locale);
  const value = key ? name[key] : undefined;
  if (value !== undefined && value !== "") return { text: value, fallback: false };
  return { text: name.en, fallback: key !== "en" };
}

/**
 * `sort_order` is a `smallint` 0…32767 (spec 05 §2.1/§2.2) and it decides what
 * the floor's grid looks like, so a typo may not be swallowed.
 *
 *  - blank  → `undefined`, meaning "don't send it": the row keeps the order it
 *    has (or, on create, takes the server's default 100). It emphatically does
 *    NOT mean 0, which would jump the row to the head of the floor grid.
 *  - anything that is not a whole number in range → `invalid`, and the caller
 *    refuses to save and says so. Silently dropping it would leave the operator
 *    looking at a screen that says it saved and a floor whose order never
 *    changed.
 */
export type ParsedSortOrder = { ok: true; value: number | undefined } | { ok: false };

export function parseSortOrder(raw: string): ParsedSortOrder {
  const text = raw.trim();
  if (text === "") return { ok: true, value: undefined };
  if (!/^\d{1,5}$/.test(text)) return { ok: false };
  const value = Number(text);
  return value <= 32767 ? { ok: true, value } : { ok: false };
}

export function WasteSettingsScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useWastePermissions();
  const [tab, setTab] = useState<Tab>("categories");

  const catalog = useWasteCatalog();
  const classes = useRemnantPriceClasses();
  const settings = useWasteSettings(perms.canReadSettings || perms.isFeedError);

  useEffect(() => {
    const err = catalog.error ?? classes.error ?? settings.error;
    if (err instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [catalog.error, classes.error, settings.error, navigate]);

  const canManageCatalog = perms.has("waste.catalog.manage");
  const canManageSettings = perms.has("waste.settings.manage");

  return (
    <section className="waste-settings">
      <PageHeader title={t("waste.settings.title")} />
      <p className="waste-hint">{t("waste.settings.intro")}</p>

      {!canManageCatalog && perms.isResolved ? (
        <p className="waste-note" role="note">
          {t("waste.settings.readOnly")}
        </p>
      ) : null}

      <NavTabs
        data-testid="waste-settings-tabs"
        label={t("waste.settings.tabsAria")}
        active={tab}
        onSelect={(id) => {
          setTab(id as Tab);
        }}
        tabs={(["categories", "causes", "classes", "matcher"] as const).map((key) => ({
          id: key,
          label: t(`waste.settings.tab.${key}`),
        }))}
      />

      {tab === "categories" ? (
        <CategoriesTab
          rows={catalog.data?.categories ?? []}
          pending={catalog.isPending}
          error={catalog.error}
          onRetry={() => void catalog.refetch()}
          canManage={canManageCatalog}
          locale={i18n.language}
        />
      ) : null}

      {tab === "causes" ? (
        <CausesTab
          rows={catalog.data?.causes ?? []}
          categories={catalog.data?.categories ?? []}
          pending={catalog.isPending}
          error={catalog.error}
          onRetry={() => void catalog.refetch()}
          canManage={canManageCatalog}
          locale={i18n.language}
        />
      ) : null}

      {tab === "classes" ? (
        <PriceClassesTab
          rows={classes.data ?? []}
          pending={classes.isPending}
          error={classes.error}
          onRetry={() => void classes.refetch()}
          canManage={canManageCatalog}
          locale={i18n.language}
        />
      ) : null}

      {tab === "matcher" ? (
        <MatcherTab
          settings={settings.data}
          pending={settings.isPending}
          error={settings.error}
          onRetry={() => void settings.refetch()}
          canEdit={canManageSettings}
          canRead={perms.canReadSettings}
          isResolved={perms.isResolved}
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// the lock
// ---------------------------------------------------------------------------

function LockChip({ source }: { source: string }) {
  const { t } = useTranslation();
  if (source === "vertical_pack")
    return <StatusChip variant="accent">{t("waste.settings.fromPack")}</StatusChip>;
  if (source === "system")
    return <StatusChip variant="accent">{t("waste.settings.fromPlatform")}</StatusChip>;
  return <StatusChip variant="neutral">{t("waste.settings.tenantOwned")}</StatusChip>;
}

/** The unlock gesture. Shown ONLY for a locked row, and it explains the trade
 * before it is made — this is the one control on the screen whose consequence
 * is invisible afterwards. */
function CustomizeButton({ onUnlock, label }: { onUnlock: () => void; label: string }) {
  const { t } = useTranslation();
  return (
    <Button size="sm" onClick={onUnlock} title={t("waste.settings.customizeHint")}>
      {label}
    </Button>
  );
}

/** The archive control. Offered on every row — see the header note: `is_active`
 * is on no pack-owned column list, so turning a pack row off costs the tenant
 * nothing and the row keeps receiving pack corrections. */
function ActiveToggle({
  code,
  isActive,
  disabled,
  onToggle,
}: {
  code: string;
  isActive: boolean;
  disabled: boolean;
  onToggle: (next: boolean) => void;
}) {
  const { t } = useTranslation();
  // Rev-7: the archive control sits behind the row's "More" button, so a row
  // carries one visible action instead of two stacked ones.
  return (
    <RowMenu label={t("waste.settings.rowMenu", { code })} data-testid="waste-row-menu">
      <Button
        size="sm"
        disabled={disabled}
        onClick={() => {
          onToggle(!isActive);
        }}
      >
        {isActive ? t("waste.common.deactivate") : t("waste.common.activate")}
      </Button>
    </RowMenu>
  );
}

/** The read-only render of a name cell: the translated name, plus the marker
 * when the viewer's locale has none and they are reading the English. */
function NameCell({ name, locale }: { name: LocaleName; locale: string }) {
  const { t } = useTranslation();
  const shown = displayName(name, locale);
  return (
    <>
      {shown.text}
      {shown.fallback ? (
        <span title={t("waste.settings.noTranslationHint")}>
          <StatusChip variant="neutral">{t("waste.settings.noTranslation")}</StatusChip>
        </span>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// categories
// ---------------------------------------------------------------------------

const DISPOSITIONS = ["pending", "reuse", "regrind", "sell", "dispose"] as const;

interface CategoryDraft {
  name: LocaleName;
  disposition: Disposition;
  recoverable: boolean;
  requiresDimensions: boolean;
  requiresCause: boolean;
  sortOrder: string;
}

/** A new category starts on the COLUMN DEFAULTS of spec 05 §2.1, not on
 * whatever is falsy: `recoverable` and `requires_cause` default true there, and
 * a form that quietly starts them false would create rows the server would have
 * created differently. */
const BLANK_CATEGORY: CategoryDraft = {
  name: { en: "" },
  disposition: "pending",
  recoverable: true,
  requiresDimensions: false,
  requiresCause: true,
  sortOrder: "",
};

function categoryDraftOf(row: WasteCategory): CategoryDraft {
  return {
    name: localeNameOf(row),
    disposition: row.default_disposition,
    recoverable: row.recoverable,
    requiresDimensions: row.requires_dimensions,
    requiresCause: row.requires_cause,
    sortOrder: String(row.sort_order),
  };
}

/** `exactOptionalPropertyTypes` is on: an absent `sort_order` must be an ABSENT
 * KEY, not the value `undefined`. */
function categoryUpdateBody(draft: CategoryDraft, sortOrder: number | undefined) {
  const body: WasteCategoryUpdate = {
    name_i18n: compact(draft.name),
    default_disposition: draft.disposition,
    recoverable: draft.recoverable,
    requires_dimensions: draft.requiresDimensions,
    requires_cause: draft.requiresCause,
  };
  if (sortOrder !== undefined) body.sort_order = sortOrder;
  return body;
}

function categoryCreateBody(code: string, draft: CategoryDraft, sortOrder: number | undefined) {
  const body: WasteCategoryCreate = {
    code,
    name_i18n: compact(draft.name),
    default_disposition: draft.disposition,
    recoverable: draft.recoverable,
    requires_dimensions: draft.requiresDimensions,
    requires_cause: draft.requiresCause,
  };
  if (sortOrder !== undefined) body.sort_order = sortOrder;
  return body;
}

function CategoriesTab({
  rows,
  pending,
  error,
  onRetry,
  canManage,
  locale,
}: {
  rows: readonly WasteCategory[];
  pending: boolean;
  error: unknown;
  onRetry: () => void;
  canManage: boolean;
  locale: string;
}) {
  const { t } = useTranslation();
  const update = useUpdateWasteCategory();
  const create = useCreateWasteCategory();
  const [unlocked, setUnlocked] = useState<ReadonlySet<string>>(new Set());
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<CategoryDraft>(BLANK_CATEGORY);
  const [adding, setAdding] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  // The floor grid's order (§4 S8). `code` breaks a tie so two rows sharing a
  // sort_order never swap places between renders.
  const sorted = useMemo(
    () => [...rows].sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code)),
    [rows],
  );
  // Rev-7: the pack's and the platform's rows always show; the tenant's
  // page 25 at a time (D-561), with a search. A row being edited or created
  // in this visit stays on screen (lib/catalog-page.ts).
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [pinnedCodes, setPinnedCodes] = useState<ReadonlySet<string>>(new Set());
  const editingCode = sorted.find((row) => row.id === editing)?.code;
  const shown = catalogPage({
    rows: sorted,
    page,
    query,
    text: (row) => Object.values(localeNameOf(row)).join(" "),
    pinned: editingCode === undefined ? pinnedCodes : new Set([...pinnedCodes, editingCode]),
  });

  const startEdit = (row: WasteCategory) => {
    setAdding(false);
    setFormError(null);
    setEditing(row.id);
    setDraft(categoryDraftOf(row));
  };

  const startAdd = () => {
    setEditing(null);
    setFormError(null);
    setDraft(BLANK_CATEGORY);
    setNewCode("");
    setAdding(true);
  };

  const columns: DataTableColumn<WasteCategory>[] = [
    {
      key: "code",
      header: t("waste.settings.colCode"),
      id: true,
      render: (row) => <code>{row.code}</code>,
    },
    {
      key: "sort_order",
      header: t("waste.settings.colSortOrder"),
      numeric: true,
      render: (row) =>
        editing === row.id ? (
          <input
            className="ds-input sort-input"
            value={draft.sortOrder}
            inputMode="numeric"
            aria-label={t("waste.settings.colSortOrder")}
            onChange={(e) => {
              setDraft({ ...draft, sortOrder: e.target.value });
            }}
          />
        ) : (
          row.sort_order
        ),
    },
    {
      key: "name",
      header: t("waste.settings.colName"),
      render: (row) =>
        editing === row.id ? (
          <LocaleFields
            value={draft.name}
            onChange={(name) => {
              setDraft({ ...draft, name });
            }}
          />
        ) : (
          <NameCell name={localeNameOf(row)} locale={locale} />
        ),
    },
    {
      key: "default_disposition",
      header: t("waste.settings.colDisposition"),
      render: (row) =>
        editing === row.id ? (
          <Select
            value={draft.disposition}
            aria-label={t("waste.settings.colDisposition")}
            onChange={(e) => {
              setDraft({ ...draft, disposition: e.target.value as Disposition });
            }}
          >
            {DISPOSITIONS.map((d) => (
              <option key={d} value={d}>
                {t(`waste.disposition.${d}`)}
              </option>
            ))}
          </Select>
        ) : (
          t(`waste.disposition.${row.default_disposition}`)
        ),
    },
    {
      key: "flags",
      header: t("waste.settings.colFlags"),
      render: (row) =>
        editing === row.id ? (
          <CategoryFlagFields
            draft={draft}
            onChange={(next) => {
              setDraft(next);
            }}
          />
        ) : (
          <>
            <StatusChip variant="neutral">
              {row.requires_dimensions
                ? t("waste.settings.flagDimensions")
                : t("waste.settings.flagNoDimensions")}
            </StatusChip>{" "}
            <StatusChip variant="neutral">
              {row.requires_cause ? t("waste.settings.flagCause") : t("waste.settings.flagNoCause")}
            </StatusChip>{" "}
            <StatusChip variant="neutral">
              {row.recoverable
                ? t("waste.settings.flagRecoverable")
                : t("waste.settings.flagNotRecoverable")}
            </StatusChip>
          </>
        ),
    },
    {
      key: "source",
      header: t("waste.settings.colOwner"),
      render: (row) => <LockChip source={row.source} />,
    },
    {
      key: "actions",
      header: t("waste.settings.colActions"),
      actions: true,
      render: (row) => {
        if (!canManage) return null;
        if (editing === row.id) {
          return (
            <div className="waste-row-actions">
              <Button
                variant="primary"
                size="sm"
                disabled={update.isPending || draft.name.en.trim() === ""}
                onClick={() => {
                  const order = parseSortOrder(draft.sortOrder);
                  if (!order.ok) {
                    setFormError(t("waste.settings.sortOrderInvalid"));
                    return;
                  }
                  setFormError(null);
                  update.mutate(
                    { id: row.id, body: categoryUpdateBody(draft, order.value) },
                    {
                      onSuccess: () => {
                        setEditing(null);
                      },
                    },
                  );
                }}
              >
                {t("waste.common.save")}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setEditing(null);
                  setFormError(null);
                }}
              >
                {t("waste.common.cancel")}
              </Button>
            </div>
          );
        }
        const isUnlocked = !row.locked || unlocked.has(row.id);
        return (
          <div className="waste-row-actions">
            {isUnlocked ? (
              <Button
                size="sm"
                onClick={() => {
                  startEdit(row);
                }}
              >
                {t("waste.common.edit")}
              </Button>
            ) : (
              <CustomizeButton
                label={t("waste.settings.customize")}
                onUnlock={() => {
                  setUnlocked(new Set([...unlocked, row.id]));
                }}
              />
            )}
            <ActiveToggle
              code={row.code}
              isActive={row.is_active}
              disabled={update.isPending}
              onToggle={(next) => {
                update.mutate({ id: row.id, body: { is_active: next } });
              }}
            />
          </div>
        );
      },
    },
  ];

  if (error && !(error instanceof UnauthorizedError)) {
    return (
      <ErrorState
        title={t("waste.settings.errorCatalog")}
        detail={(error as Error).message}
        retryLabel={t("waste.common.retry")}
        onRetry={onRetry}
        requestId={requestIdOf(error)}
        requestIdLabel={t("waste.common.requestId")}
      />
    );
  }
  if (pending) return <TableSkeleton rows={6} cols={5} label={t("waste.settings.loading")} />;
  if (sorted.length === 0 && !adding) {
    return (
      <EmptyState
        title={t("waste.settings.emptyCategoriesTitle")}
        body={t("waste.settings.emptyCategoriesBody")}
        {...(canManage
          ? {
              ctaLabel: t("waste.settings.addCategory"),
              onCta: () => {
                setDraft(BLANK_CATEGORY);
                setAdding(true);
              },
            }
          : {})}
      />
    );
  }

  return (
    <>
      {sorted.length > CATALOG_PAGE_SIZE ? (
        <FilterBar
          data-testid="waste-settings-category-filter"
          search={{
            value: query,
            onChange: (next) => {
              setQuery(next);
              setPage(1);
            },
            label: t("waste.settings.search"),
            placeholder: t("waste.settings.searchPlaceholder"),
          }}
        />
      ) : null}
      <DataTable
        columns={columns}
        rows={shown.rows}
        rowKey={(row) => row.id}
        empty={<p className="waste-hint">{t("waste.settings.noMatch")}</p>}
        caption={t("waste.settings.tab.categories")}
        pager={
          shown.pageCount > 1
            ? {
                range: t("waste.settings.pageRange", {
                  from: shown.from,
                  to: shown.to,
                  total: shown.total,
                }),
                page: shown.page,
                pageCount: shown.pageCount,
                onPage: setPage,
                prevLabel: t("waste.common.prev"),
                nextLabel: t("waste.common.next"),
              }
            : undefined
        }
        rowTestId="category-row"
        rowProps={(row) => ({ "data-testid": `category-row-${row.code}` })}
        rowClassName={(row) => (row.is_active ? undefined : "is-inactive")}
      />

      {formError !== null ? <p className="form-error">{formError}</p> : null}
      {update.error ? <p className="form-error">{update.error.message}</p> : null}

      {canManage ? (
        adding ? (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              const order = parseSortOrder(draft.sortOrder);
              if (!order.ok) {
                setFormError(t("waste.settings.sortOrderInvalid"));
                return;
              }
              setFormError(null);
              const createdCode = newCode.trim().toUpperCase();
              create.mutate(categoryCreateBody(createdCode, draft, order.value), {
                onSuccess: () => {
                  setPinnedCodes(new Set([...pinnedCodes, createdCode]));
                  setAdding(false);
                  setNewCode("");
                  setDraft(BLANK_CATEGORY);
                },
              });
            }}
          >
            <Field label={t("waste.settings.colCode")}>
              <input
                value={newCode}
                onChange={(e) => {
                  setNewCode(e.target.value);
                }}
                placeholder="CABLE_DUST"
                required
              />
            </Field>
            <LocaleFields
              value={draft.name}
              onChange={(name) => {
                setDraft({ ...draft, name });
              }}
            />
            <p className="waste-hint">{t("waste.settings.codeIsForever")}</p>
            <Field label={t("waste.settings.colDisposition")}>
              <select
                value={draft.disposition}
                onChange={(e) => {
                  setDraft({ ...draft, disposition: e.target.value as Disposition });
                }}
              >
                {DISPOSITIONS.map((d) => (
                  <option key={d} value={d}>
                    {t(`waste.disposition.${d}`)}
                  </option>
                ))}
              </select>
            </Field>
            <CategoryFlagFields
              draft={draft}
              onChange={(next) => {
                setDraft(next);
              }}
            />
            <Field label={t("waste.settings.colSortOrder")}>
              <input
                className="sort-input"
                value={draft.sortOrder}
                inputMode="numeric"
                onChange={(e) => {
                  setDraft({ ...draft, sortOrder: e.target.value });
                }}
              />
            </Field>
            <p className="waste-hint">{t("waste.settings.sortOrderHint")}</p>
            {create.error ? <p className="form-error">{create.error.message}</p> : null}
            <div className="waste-row-actions">
              <Button type="submit" variant="primary" disabled={create.isPending}>
                {t("waste.common.create")}
              </Button>
              <Button
                onClick={() => {
                  setAdding(false);
                  setFormError(null);
                }}
              >
                {t("waste.common.cancel")}
              </Button>
            </div>
          </form>
        ) : (
          <div className="waste-row-actions">
            <Button onClick={startAdd}>{t("waste.settings.addCategory")}</Button>
          </div>
        )
      ) : null}
    </>
  );
}

/** The three capture flags (§2.1). They travel together because they describe
 * one thing — what the floor is asked for when this type is tapped. */
function CategoryFlagFields({
  draft,
  onChange,
}: {
  draft: CategoryDraft;
  onChange: (next: CategoryDraft) => void;
}) {
  const { t } = useTranslation();
  return (
    <fieldset className="check-group">
      <legend>{t("waste.settings.colFlags")}</legend>
      <CheckboxField
        label={t("waste.settings.flagDimensions")}
        checked={draft.requiresDimensions}
        onChange={(requiresDimensions) => {
          onChange({ ...draft, requiresDimensions });
        }}
      />
      <CheckboxField
        label={t("waste.settings.flagCause")}
        checked={draft.requiresCause}
        onChange={(requiresCause) => {
          onChange({ ...draft, requiresCause });
        }}
      />
      <CheckboxField
        label={t("waste.settings.flagRecoverable")}
        checked={draft.recoverable}
        onChange={(recoverable) => {
          onChange({ ...draft, recoverable });
        }}
      />
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// causes
// ---------------------------------------------------------------------------

interface CauseDraft {
  name: LocaleName;
  categoryIds: readonly string[];
  blamesMachine: boolean;
  blamesSupplier: boolean;
  sortOrder: string;
}

const BLANK_CAUSE: CauseDraft = {
  name: { en: "" },
  categoryIds: [],
  blamesMachine: false,
  blamesSupplier: false,
  sortOrder: "",
};

function causeDraftOf(row: WasteCauseCode): CauseDraft {
  return {
    name: localeNameOf(row),
    categoryIds: row.category_ids,
    blamesMachine: row.blames_machine,
    blamesSupplier: row.blames_supplier,
    sortOrder: String(row.sort_order),
  };
}

function causeUpdateBody(draft: CauseDraft, sortOrder: number | undefined) {
  const body: WasteCauseUpdate = {
    name_i18n: compact(draft.name),
    category_ids: [...draft.categoryIds],
    blames_machine: draft.blamesMachine,
    blames_supplier: draft.blamesSupplier,
  };
  if (sortOrder !== undefined) body.sort_order = sortOrder;
  return body;
}

function causeCreateBody(code: string, draft: CauseDraft, sortOrder: number | undefined) {
  const body: WasteCauseCreate = {
    code,
    name_i18n: compact(draft.name),
    category_ids: [...draft.categoryIds],
    blames_machine: draft.blamesMachine,
    blames_supplier: draft.blamesSupplier,
  };
  if (sortOrder !== undefined) body.sort_order = sortOrder;
  return body;
}

function CausesTab({
  rows,
  categories,
  pending,
  error,
  onRetry,
  canManage,
  locale,
}: {
  rows: readonly WasteCauseCode[];
  categories: readonly WasteCategory[];
  pending: boolean;
  error: unknown;
  onRetry: () => void;
  canManage: boolean;
  locale: string;
}) {
  const { t } = useTranslation();
  const update = useUpdateWasteCause();
  const create = useCreateWasteCause();
  const [unlocked, setUnlocked] = useState<ReadonlySet<string>>(new Set());
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [draft, setDraft] = useState<CauseDraft>(BLANK_CAUSE);
  const [formError, setFormError] = useState<string | null>(null);

  const categoryCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categories) map.set(c.id, c.code);
    return map;
  }, [categories]);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code)),
    [rows],
  );
  // Rev-7: the pack's and the platform's rows always show; the tenant's
  // page 25 at a time (D-561), with a search. A row being edited or created
  // in this visit stays on screen (lib/catalog-page.ts).
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [pinnedCodes, setPinnedCodes] = useState<ReadonlySet<string>>(new Set());
  const editingCode = sorted.find((row) => row.id === editing)?.code;
  const shown = catalogPage({
    rows: sorted,
    page,
    query,
    text: (row) => Object.values(localeNameOf(row)).join(" "),
    pinned: editingCode === undefined ? pinnedCodes : new Set([...pinnedCodes, editingCode]),
  });

  const startEdit = (row: WasteCauseCode) => {
    setAdding(false);
    setFormError(null);
    setEditing(row.id);
    setDraft(causeDraftOf(row));
  };

  const columns: DataTableColumn<WasteCauseCode>[] = [
    {
      key: "code",
      header: t("waste.settings.colCode"),
      id: true,
      render: (row) => <code>{row.code}</code>,
    },
    {
      key: "sort_order",
      header: t("waste.settings.colSortOrder"),
      numeric: true,
      render: (row) =>
        editing === row.id ? (
          <input
            className="ds-input sort-input"
            value={draft.sortOrder}
            inputMode="numeric"
            aria-label={t("waste.settings.colSortOrder")}
            onChange={(e) => {
              setDraft({ ...draft, sortOrder: e.target.value });
            }}
          />
        ) : (
          row.sort_order
        ),
    },
    {
      key: "name",
      header: t("waste.settings.colName"),
      render: (row) =>
        editing === row.id ? (
          <LocaleFields
            value={draft.name}
            onChange={(name) => {
              setDraft({ ...draft, name });
            }}
          />
        ) : (
          <NameCell name={localeNameOf(row)} locale={locale} />
        ),
    },
    {
      key: "category_ids",
      header: t("waste.settings.colAppliesTo"),
      render: (row) =>
        editing === row.id ? (
          <CategoryPicker
            categories={categories}
            selected={draft.categoryIds}
            locale={locale}
            onChange={(categoryIds) => {
              setDraft({ ...draft, categoryIds });
            }}
          />
        ) : row.category_ids.length === 0 ? (
          t("waste.settings.allCategories")
        ) : (
          row.category_ids.map((id) => categoryCode.get(id) ?? id).join(", ")
        ),
    },
    {
      key: "blame",
      header: t("waste.settings.colBlame"),
      render: (row) =>
        editing === row.id ? (
          <fieldset className="check-group">
            <legend>{t("waste.settings.colBlame")}</legend>
            <CheckboxField
              label={t("waste.settings.blamesMachine")}
              checked={draft.blamesMachine}
              onChange={(blamesMachine) => {
                setDraft({ ...draft, blamesMachine });
              }}
            />
            <CheckboxField
              label={t("waste.settings.blamesSupplier")}
              checked={draft.blamesSupplier}
              onChange={(blamesSupplier) => {
                setDraft({ ...draft, blamesSupplier });
              }}
            />
          </fieldset>
        ) : (
          <>
            {row.blames_machine ? (
              <StatusChip variant="neutral">{t("waste.settings.blamesMachine")}</StatusChip>
            ) : null}{" "}
            {row.blames_supplier ? (
              <StatusChip variant="neutral">{t("waste.settings.blamesSupplier")}</StatusChip>
            ) : null}
            {!row.blames_machine && !row.blames_supplier ? t("waste.settings.blamesNobody") : null}
          </>
        ),
    },
    {
      key: "source",
      header: t("waste.settings.colOwner"),
      render: (row) => <LockChip source={row.source} />,
    },
    {
      key: "actions",
      header: t("waste.settings.colActions"),
      actions: true,
      render: (row) => {
        if (!canManage) return null;
        if (editing === row.id) {
          return (
            <div className="waste-row-actions">
              <Button
                variant="primary"
                size="sm"
                disabled={update.isPending || draft.name.en.trim() === ""}
                onClick={() => {
                  const order = parseSortOrder(draft.sortOrder);
                  if (!order.ok) {
                    setFormError(t("waste.settings.sortOrderInvalid"));
                    return;
                  }
                  setFormError(null);
                  update.mutate(
                    { id: row.id, body: causeUpdateBody(draft, order.value) },
                    {
                      onSuccess: () => {
                        setEditing(null);
                      },
                    },
                  );
                }}
              >
                {t("waste.common.save")}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setEditing(null);
                  setFormError(null);
                }}
              >
                {t("waste.common.cancel")}
              </Button>
            </div>
          );
        }
        const isUnlocked = !row.locked || unlocked.has(row.id);
        return (
          <div className="waste-row-actions">
            {isUnlocked ? (
              <Button
                size="sm"
                onClick={() => {
                  startEdit(row);
                }}
              >
                {t("waste.common.edit")}
              </Button>
            ) : (
              <CustomizeButton
                label={t("waste.settings.customize")}
                onUnlock={() => {
                  setUnlocked(new Set([...unlocked, row.id]));
                }}
              />
            )}
            <ActiveToggle
              code={row.code}
              isActive={row.is_active}
              disabled={update.isPending}
              onToggle={(next) => {
                update.mutate({ id: row.id, body: { is_active: next } });
              }}
            />
          </div>
        );
      },
    },
  ];

  if (error && !(error instanceof UnauthorizedError)) {
    return (
      <ErrorState
        title={t("waste.settings.errorCatalog")}
        detail={(error as Error).message}
        retryLabel={t("waste.common.retry")}
        onRetry={onRetry}
        requestId={requestIdOf(error)}
        requestIdLabel={t("waste.common.requestId")}
      />
    );
  }
  if (pending) return <TableSkeleton rows={6} cols={4} label={t("waste.settings.loading")} />;
  if (sorted.length === 0 && !adding) {
    return (
      <EmptyState
        title={t("waste.settings.emptyCausesTitle")}
        body={t("waste.settings.emptyCausesBody")}
        {...(canManage
          ? {
              ctaLabel: t("waste.settings.addCause"),
              onCta: () => {
                setDraft(BLANK_CAUSE);
                setAdding(true);
              },
            }
          : {})}
      />
    );
  }

  return (
    <>
      {sorted.length > CATALOG_PAGE_SIZE ? (
        <FilterBar
          data-testid="waste-settings-cause-filter"
          search={{
            value: query,
            onChange: (next) => {
              setQuery(next);
              setPage(1);
            },
            label: t("waste.settings.search"),
            placeholder: t("waste.settings.searchPlaceholder"),
          }}
        />
      ) : null}
      <DataTable
        columns={columns}
        rows={shown.rows}
        rowKey={(row) => row.id}
        empty={<p className="waste-hint">{t("waste.settings.noMatch")}</p>}
        caption={t("waste.settings.tab.causes")}
        pager={
          shown.pageCount > 1
            ? {
                range: t("waste.settings.pageRange", {
                  from: shown.from,
                  to: shown.to,
                  total: shown.total,
                }),
                page: shown.page,
                pageCount: shown.pageCount,
                onPage: setPage,
                prevLabel: t("waste.common.prev"),
                nextLabel: t("waste.common.next"),
              }
            : undefined
        }
        rowTestId="cause-row"
        rowProps={(row) => ({ "data-testid": `cause-row-${row.code}` })}
        rowClassName={(row) => (row.is_active ? undefined : "is-inactive")}
      />

      {formError !== null ? <p className="form-error">{formError}</p> : null}
      {update.error ? <p className="form-error">{update.error.message}</p> : null}

      {canManage ? (
        adding ? (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              const order = parseSortOrder(draft.sortOrder);
              if (!order.ok) {
                setFormError(t("waste.settings.sortOrderInvalid"));
                return;
              }
              setFormError(null);
              const createdCode = newCode.trim().toUpperCase();
              create.mutate(causeCreateBody(createdCode, draft, order.value), {
                onSuccess: () => {
                  setPinnedCodes(new Set([...pinnedCodes, createdCode]));
                  setAdding(false);
                  setNewCode("");
                  setDraft(BLANK_CAUSE);
                },
              });
            }}
          >
            <Field label={t("waste.settings.colCode")}>
              <input
                value={newCode}
                onChange={(e) => {
                  setNewCode(e.target.value);
                }}
                required
              />
            </Field>
            <LocaleFields
              value={draft.name}
              onChange={(name) => {
                setDraft({ ...draft, name });
              }}
            />
            <p className="waste-hint">{t("waste.settings.codeIsForever")}</p>
            <CategoryPicker
              categories={categories}
              selected={draft.categoryIds}
              locale={locale}
              onChange={(categoryIds) => {
                setDraft({ ...draft, categoryIds });
              }}
            />
            <fieldset className="check-group">
              <legend>{t("waste.settings.colBlame")}</legend>
              <CheckboxField
                label={t("waste.settings.blamesMachine")}
                checked={draft.blamesMachine}
                onChange={(blamesMachine) => {
                  setDraft({ ...draft, blamesMachine });
                }}
              />
              <CheckboxField
                label={t("waste.settings.blamesSupplier")}
                checked={draft.blamesSupplier}
                onChange={(blamesSupplier) => {
                  setDraft({ ...draft, blamesSupplier });
                }}
              />
            </fieldset>
            <Field label={t("waste.settings.colSortOrder")}>
              <input
                className="sort-input"
                value={draft.sortOrder}
                inputMode="numeric"
                onChange={(e) => {
                  setDraft({ ...draft, sortOrder: e.target.value });
                }}
              />
            </Field>
            <p className="waste-hint">{t("waste.settings.sortOrderHint")}</p>
            {create.error ? <p className="form-error">{create.error.message}</p> : null}
            <div className="waste-row-actions">
              <Button type="submit" variant="primary" disabled={create.isPending}>
                {t("waste.common.create")}
              </Button>
              <Button
                onClick={() => {
                  setAdding(false);
                  setFormError(null);
                }}
              >
                {t("waste.common.cancel")}
              </Button>
            </div>
          </form>
        ) : (
          <div className="waste-row-actions">
            <Button
              onClick={() => {
                setEditing(null);
                setFormError(null);
                setDraft(BLANK_CAUSE);
                setNewCode("");
                setAdding(true);
              }}
            >
              {t("waste.settings.addCause")}
            </Button>
          </div>
        )
      ) : null}
    </>
  );
}

/**
 * §2.2 `category_ids`: which waste types may offer this reason. EMPTY MEANS
 * ALL, which is the opposite of what an empty checkbox list looks like, so the
 * box says so in words rather than leaving the operator to guess.
 */
function CategoryPicker({
  categories,
  selected,
  locale,
  onChange,
}: {
  categories: readonly WasteCategory[];
  selected: readonly string[];
  locale: string;
  onChange: (next: readonly string[]) => void;
}) {
  const { t } = useTranslation();
  const chosen = new Set(selected);
  return (
    <fieldset className="check-group is-scroll">
      <legend>{t("waste.settings.colAppliesTo")}</legend>
      <p className="waste-hint">
        {chosen.size === 0
          ? t("waste.settings.appliesToAllHint")
          : t("waste.settings.appliesToHint")}
      </p>
      {categories.map((category) => (
        <CheckboxField
          key={category.id}
          label={`${displayName(localeNameOf(category), locale).text} (${category.code})`}
          checked={chosen.has(category.id)}
          onChange={(next) => {
            onChange(
              next ? [...selected, category.id] : selected.filter((id) => id !== category.id),
            );
          }}
        />
      ))}
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// price classes
// ---------------------------------------------------------------------------

const FLOOR_POLICIES = ["above_cost", "none"] as const;

interface ClassDraft {
  name: LocaleName;
  discount: string;
  minLen: string;
  maxLen: string;
  policy: FloorPolicy;
}

/** §2.7's column default is `above_cost`: selling below carried cost needs a
 * Module 12 approval. A form that started on `none` would quietly create
 * classes that skip that approval. */
const BLANK_CLASS: ClassDraft = {
  name: { en: "" },
  discount: "",
  minLen: "",
  maxLen: "",
  policy: "above_cost",
};

function classDraftOf(row: RemnantPriceClass): ClassDraft {
  return {
    name: localeNameOf(row),
    discount: row.discount_pct,
    minLen: row.auto_assign_min_length_m ?? "",
    maxLen: row.auto_assign_max_length_m ?? "",
    policy: row.floor_price_policy,
  };
}

/** Band bounds travel as exact decimal TEXT or `null` (D-041) — never a JS
 * number. `null` is meaningful here: it clears the bound, which is how a class
 * gives its band back (§2.7 band uniqueness). */
function classUpdateBody(draft: ClassDraft): RemnantPriceClassUpdate {
  return {
    name_i18n: compact(draft.name),
    discount_pct: draft.discount,
    floor_price_policy: draft.policy,
    auto_assign_min_length_m: draft.minLen === "" ? null : draft.minLen,
    auto_assign_max_length_m: draft.maxLen === "" ? null : draft.maxLen,
  };
}

function classCreateBody(code: string, draft: ClassDraft): RemnantPriceClassCreate {
  return {
    code,
    name_i18n: compact(draft.name),
    discount_pct: draft.discount === "" ? "0" : draft.discount,
    floor_price_policy: draft.policy,
    auto_assign_min_length_m: draft.minLen === "" ? null : draft.minLen,
    auto_assign_max_length_m: draft.maxLen === "" ? null : draft.maxLen,
  };
}

function PriceClassesTab({
  rows,
  pending,
  error,
  onRetry,
  canManage,
  locale,
}: {
  rows: readonly RemnantPriceClass[];
  pending: boolean;
  error: unknown;
  onRetry: () => void;
  canManage: boolean;
  locale: string;
}) {
  const { t } = useTranslation();
  const update = useUpdatePriceClass();
  const create = useCreatePriceClass();
  const [unlocked, setUnlocked] = useState<ReadonlySet<string>>(new Set());
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [draft, setDraft] = useState<ClassDraft>(BLANK_CLASS);

  const columns: DataTableColumn<RemnantPriceClass>[] = [
    {
      key: "code",
      header: t("waste.settings.colCode"),
      id: true,
      render: (row) => <code>{row.code}</code>,
    },
    {
      key: "name",
      header: t("waste.settings.colName"),
      render: (row) =>
        editing === row.id ? (
          <LocaleFields
            value={draft.name}
            onChange={(name) => {
              setDraft({ ...draft, name });
            }}
          />
        ) : (
          <NameCell name={localeNameOf(row)} locale={locale} />
        ),
    },
    {
      key: "discount_pct",
      header: t("waste.settings.colDiscount"),
      numeric: true,
      render: (row) =>
        editing === row.id ? (
          <input
            className="ds-input"
            value={draft.discount}
            onChange={(e) => {
              setDraft({ ...draft, discount: e.target.value });
            }}
            inputMode="decimal"
            aria-label={t("waste.settings.colDiscount")}
          />
        ) : (
          t("waste.settings.pct", { value: row.discount_pct })
        ),
    },
    {
      key: "band",
      header: t("waste.settings.colBand"),
      render: (row) =>
        editing === row.id ? (
          <div className="band-fields">
            <input
              className="ds-input"
              value={draft.minLen}
              onChange={(e) => {
                setDraft({ ...draft, minLen: e.target.value });
              }}
              inputMode="decimal"
              aria-label={t("waste.settings.bandMin")}
            />
            <input
              className="ds-input"
              value={draft.maxLen}
              onChange={(e) => {
                setDraft({ ...draft, maxLen: e.target.value });
              }}
              inputMode="decimal"
              aria-label={t("waste.settings.bandMax")}
            />
          </div>
        ) : (
          bandLabel(row, (key, options) => t(key, options ?? {}))
        ),
    },
    {
      key: "floor_price_policy",
      header: t("waste.settings.colFloorPolicy"),
      render: (row) =>
        editing === row.id ? (
          <Select
            value={draft.policy}
            aria-label={t("waste.settings.colFloorPolicy")}
            onChange={(e) => {
              setDraft({ ...draft, policy: e.target.value as FloorPolicy });
            }}
          >
            {FLOOR_POLICIES.map((p) => (
              <option key={p} value={p}>
                {t(`waste.settings.floorPolicy.${p}`)}
              </option>
            ))}
          </Select>
        ) : (
          t(`waste.settings.floorPolicy.${row.floor_price_policy}`)
        ),
    },
    {
      key: "source",
      header: t("waste.settings.colOwner"),
      render: (row) => <LockChip source={row.source} />,
    },
    {
      key: "actions",
      header: t("waste.settings.colActions"),
      actions: true,
      render: (row) => {
        if (!canManage) return null;
        if (editing === row.id) {
          return (
            <div className="waste-row-actions">
              <Button
                variant="primary"
                size="sm"
                disabled={update.isPending || draft.name.en.trim() === ""}
                onClick={() => {
                  update.mutate(
                    { id: row.id, body: classUpdateBody(draft) },
                    {
                      onSuccess: () => {
                        setEditing(null);
                      },
                    },
                  );
                }}
              >
                {t("waste.common.save")}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setEditing(null);
                }}
              >
                {t("waste.common.cancel")}
              </Button>
            </div>
          );
        }
        const isUnlocked = !row.locked || unlocked.has(row.id);
        return (
          <div className="waste-row-actions">
            {isUnlocked ? (
              <Button
                size="sm"
                onClick={() => {
                  setAdding(false);
                  setEditing(row.id);
                  setDraft(classDraftOf(row));
                }}
              >
                {t("waste.common.edit")}
              </Button>
            ) : (
              <CustomizeButton
                label={t("waste.settings.customize")}
                onUnlock={() => {
                  setUnlocked(new Set([...unlocked, row.id]));
                }}
              />
            )}
            <ActiveToggle
              code={row.code}
              isActive={row.is_active}
              disabled={update.isPending}
              onToggle={(next) => {
                update.mutate({ id: row.id, body: { is_active: next } });
              }}
            />
          </div>
        );
      },
    },
  ];

  if (error && !(error instanceof UnauthorizedError)) {
    return (
      <ErrorState
        title={t("waste.settings.errorClasses")}
        detail={(error as Error).message}
        retryLabel={t("waste.common.retry")}
        onRetry={onRetry}
        requestId={requestIdOf(error)}
        requestIdLabel={t("waste.common.requestId")}
      />
    );
  }
  if (pending) return <TableSkeleton rows={4} cols={4} label={t("waste.settings.loading")} />;
  if (rows.length === 0 && !adding) {
    return (
      <EmptyState
        title={t("waste.settings.emptyClassesTitle")}
        body={t("waste.settings.emptyClassesBody")}
        {...(canManage
          ? {
              ctaLabel: t("waste.settings.addClass"),
              onCta: () => {
                setDraft(BLANK_CLASS);
                setAdding(true);
              },
            }
          : {})}
      />
    );
  }

  return (
    <>
      <p className="waste-hint">{t("waste.settings.bandHint")}</p>
      {/* The band dead-end and its documented way out (§2.7): two ACTIVE
          classes may not both claim 100 m, and turning one off frees its band.
          Said here because the 422 the server raises otherwise names a rule the
          operator has never been shown. */}
      <p className="waste-hint">{t("waste.settings.deactivateFreesBand")}</p>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        empty={null}
        caption={t("waste.settings.tab.classes")}
        rowTestId="class-row"
        rowProps={(row) => ({ "data-testid": `class-row-${row.code}` })}
        rowClassName={(row) => (row.is_active ? undefined : "is-inactive")}
      />

      {update.error ? <p className="form-error">{update.error.message}</p> : null}

      {canManage ? (
        adding ? (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate(classCreateBody(newCode.trim().toUpperCase(), draft), {
                onSuccess: () => {
                  setAdding(false);
                  setNewCode("");
                  setDraft(BLANK_CLASS);
                },
              });
            }}
          >
            <Field label={t("waste.settings.colCode")}>
              <input
                value={newCode}
                onChange={(e) => {
                  setNewCode(e.target.value);
                }}
                required
              />
            </Field>
            <LocaleFields
              value={draft.name}
              onChange={(name) => {
                setDraft({ ...draft, name });
              }}
            />
            <Field label={t("waste.settings.colDiscount")}>
              <input
                value={draft.discount}
                onChange={(e) => {
                  setDraft({ ...draft, discount: e.target.value });
                }}
                inputMode="decimal"
              />
            </Field>
            <Field label={t("waste.settings.bandMin")}>
              <input
                value={draft.minLen}
                onChange={(e) => {
                  setDraft({ ...draft, minLen: e.target.value });
                }}
                inputMode="decimal"
              />
            </Field>
            <Field label={t("waste.settings.bandMax")}>
              <input
                value={draft.maxLen}
                onChange={(e) => {
                  setDraft({ ...draft, maxLen: e.target.value });
                }}
                inputMode="decimal"
              />
            </Field>
            <Field label={t("waste.settings.colFloorPolicy")}>
              <select
                value={draft.policy}
                onChange={(e) => {
                  setDraft({ ...draft, policy: e.target.value as FloorPolicy });
                }}
              >
                {FLOOR_POLICIES.map((p) => (
                  <option key={p} value={p}>
                    {t(`waste.settings.floorPolicy.${p}`)}
                  </option>
                ))}
              </select>
            </Field>
            <p className="waste-hint">{t("waste.settings.floorPolicyHint")}</p>
            {create.error ? <p className="form-error">{create.error.message}</p> : null}
            <div className="waste-row-actions">
              <Button type="submit" variant="primary" disabled={create.isPending}>
                {t("waste.common.create")}
              </Button>
              <Button
                onClick={() => {
                  setAdding(false);
                }}
              >
                {t("waste.common.cancel")}
              </Button>
            </div>
          </form>
        ) : (
          <div className="waste-row-actions">
            <Button
              onClick={() => {
                setEditing(null);
                setDraft(BLANK_CLASS);
                setNewCode("");
                setAdding(true);
              }}
            >
              {t("waste.settings.addClass")}
            </Button>
          </div>
        )
      ) : null}
    </>
  );
}

function bandLabel(
  row: RemnantPriceClass,
  t: (key: string, options?: Record<string, string>) => string,
): string {
  const { auto_assign_min_length_m: min, auto_assign_max_length_m: max } = row;
  if (min === null && max === null) return t("waste.settings.bandNone");
  if (min === null) return t("waste.settings.bandBelow", { max: max ?? "" });
  if (max === null) return t("waste.settings.bandAbove", { min });
  return t("waste.settings.bandRange", { min, max });
}

// ---------------------------------------------------------------------------
// matcher / variance settings
// ---------------------------------------------------------------------------

const SETTING_FIELDS = [
  "exact_tolerance_m",
  "max_overage_pct",
  "reservation_ttl_hours",
  "variance_tolerance_default_pct",
  "wire_diameter_tolerance_mm",
  "min_remnant_track_length_m",
  "recovery_target_pct_default",
] as const;

function MatcherTab({
  settings,
  pending,
  error,
  onRetry,
  canEdit,
  canRead,
  isResolved,
}: {
  settings: WasteSettings | undefined;
  pending: boolean;
  error: unknown;
  onRetry: () => void;
  canEdit: boolean;
  canRead: boolean;
  isResolved: boolean;
}) {
  const { t } = useTranslation();
  const update = useUpdateWasteSettings();
  const [form, setForm] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!settings) return;
    setForm({
      exact_tolerance_m: settings.exact_tolerance_m,
      max_overage_pct: settings.max_overage_pct,
      reservation_ttl_hours: String(settings.reservation_ttl_hours),
      variance_tolerance_default_pct: settings.variance_tolerance_default_pct,
      wire_diameter_tolerance_mm: settings.wire_diameter_tolerance_mm,
      min_remnant_track_length_m: settings.min_remnant_track_length_m,
      recovery_target_pct_default: settings.recovery_target_pct_default,
    });
  }, [settings]);

  if (isResolved && !canRead) {
    return (
      <EmptyState
        title={t("waste.settings.noSettingsAccessTitle")}
        body={t("waste.settings.noSettingsAccessBody")}
      />
    );
  }
  if (error && !(error instanceof UnauthorizedError)) {
    return (
      <ErrorState
        title={t("waste.settings.errorSettings")}
        detail={(error as Error).message}
        retryLabel={t("waste.common.retry")}
        onRetry={onRetry}
        requestId={requestIdOf(error)}
        requestIdLabel={t("waste.common.requestId")}
      />
    );
  }
  if (pending || !settings) {
    return <TableSkeleton rows={5} cols={2} label={t("waste.settings.loading")} />;
  }

  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        // Only fields the form actually holds travel: a PUT is a partial patch
        // (§5 row 58 "only the fields present change"), and sending `undefined`
        // as a value would be sending a decision nobody made.
        const body: Record<string, string | number> = {};
        for (const field of SETTING_FIELDS) {
          const value = form[field];
          if (value === undefined || value === "") continue;
          body[field] = field === "reservation_ttl_hours" ? Number(value) : value;
        }
        update.mutate(body);
      }}
    >
      {/* D-069. `pack_defaults_customized` latches true the first time either
          pack-seeded knob moves and never comes back, so the trade has to be
          stated BEFORE the tenant spends it — a receipt afterwards is not a
          choice. Read-only on the wire: it is a consequence of an edit, never
          an input to one. */}
      <p className={settings.pack_defaults_customized ? "waste-note" : "waste-hint"}>
        {t(
          settings.pack_defaults_customized
            ? "waste.settings.packDefaults.customized"
            : "waste.settings.packDefaults.pristine",
        )}
      </p>
      {SETTING_FIELDS.map((field) => (
        <Field key={field} label={t(`waste.settings.field.${field}`)}>
          <input
            value={form[field] ?? ""}
            inputMode="decimal"
            disabled={!canEdit}
            onChange={(e) => {
              setForm({ ...form, [field]: e.target.value });
            }}
          />
          <span className="waste-hint">{t(`waste.settings.fieldHint.${field}`)}</span>
        </Field>
      ))}
      {update.error ? <p className="form-error">{update.error.message}</p> : null}
      {update.isSuccess ? <p className="form-ok">{t("waste.settings.saved")}</p> : null}
      {canEdit ? (
        <div className="waste-row-actions">
          <Button
            type="submit"
            variant="primary"
            busy={update.isPending}
            busyLabel={t("waste.common.saving")}
          >
            {t("waste.common.save")}
          </Button>
        </div>
      ) : (
        <p className="waste-hint">{t("waste.settings.settingsReadOnly")}</p>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// shared bits
// ---------------------------------------------------------------------------

function LocaleFields({
  value,
  onChange,
}: {
  value: LocaleName;
  onChange: (next: LocaleName) => void;
}) {
  const { t } = useTranslation();
  const more = useStickyOpen(LOCALES.some((l) => !ALWAYS_SHOWN.has(l) && (value[l] ?? "") !== ""));
  const field = (locale: Locale) => (
    <Field key={locale} label={t(`waste.locale.${locale}`)}>
      <input
        value={value[locale] ?? ""}
        required={locale === "en"}
        placeholder={locale === "en" ? "" : t("waste.settings.optionalLocale")}
        onChange={(e) => {
          onChange({ ...value, [locale]: e.target.value });
        }}
      />
    </Field>
  );
  return (
    <div className="locale-fields">
      {LOCALES.filter((locale) => ALWAYS_SHOWN.has(locale)).map(field)}
      {/* Rev-7: the other languages sit behind one disclosure, open when one
          of them already holds a name, so six boxes never crowd the form. It
          never closes itself: emptying the last name used to fold it under
          the cursor. */}
      <details className="locale-fields-more" open={more.open} onToggle={more.onToggle}>
        <summary>{t("waste.settings.otherLanguages")}</summary>
        {LOCALES.filter((locale) => !ALWAYS_SHOWN.has(locale)).map(field)}
      </details>
    </div>
  );
}

/** Drop the locales the author left blank — an empty string would be stored as
 * a "translation" that renders as nothing on the floor. */
export function compact(name: LocaleName): LocaleName {
  const out: LocaleName = { en: name.en.trim() };
  for (const locale of LOCALES) {
    if (locale === "en") continue;
    const v = name[locale]?.trim();
    if (v !== undefined && v !== "") out[locale] = v;
  }
  return out;
}

export type { LocaleName };
export type LocaleKey = Locale;
